import { Link } from "react-router";
import { ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Card, PageLoader, SectionTitle, Stat } from "@/components/ui";
import { Confetti, Points, ProgressBar } from "@/components/bits";
import { useKidJobs, useKidResponsibilities, useKidSummary } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { hourlyRate, JobCard, useUnlockCelebration } from "./common";
import { ResponsibilityRow } from "./Today";

/** Today for older kids: the numbers first, then what's left to do and what pays best. */
export function OlderToday() {
  const { user, family } = useSession();
  const summary = useKidSummary(user.id);
  const resp = useKidResponsibilities();
  const jobs = useKidJobs();
  const celebrate = useUnlockCelebration(resp.data?.gate);

  if (!summary.data || !resp.data || !jobs.data) return <PageLoader />;
  const s = summary.data;
  const gate = resp.data.gate;
  const due = resp.data.items.filter((i) => i.due);
  const left = due.filter((i) => i.completion?.status !== "done" && i.completion?.status !== "approved");
  const active = jobs.data.jobs.filter((j) => j.activeRun);
  const open = jobs.data.jobs
    .filter((j) => !j.activeRun && (j.available || j.locked))
    .sort((a, b) => Number(b.workItOff) - Number(a.workItOff) || (hourlyRate(b) ?? -1) - (hourlyRate(a) ?? -1));
  const goal = s.primaryGoal;
  const remaining = goal ? Math.max(0, Math.round(goal.price * (1 - goal.progress))) : 0;
  const weeks = s.earnedThisWeek > 0 && remaining > 0 ? Math.ceil(remaining / s.earnedThisWeek) : null;

  return (
    <div>
      {celebrate > 0 && <Confetti key={celebrate} />}
      <PageHeader title={user.name} subtitle={new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} bell="/kid/notifications" />

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Stat label="Balance" value={<Points value={s.balance} family={family} />} tone={s.balance < 0 ? "danger" : "coin"} />
        <Stat label="Can spend" value={<Points value={s.available} family={family} />} />
        <Stat label="This week" value={<Points value={s.earnedThisWeek} family={family} signed />} tone="brand" />
      </div>

      {goal && (
        <Link to="/kid/goals" className="mt-3 block">
          <Card className="p-3">
            <div className="flex items-center justify-between gap-2 text-sm font-bold">
              <span className="truncate">
                {goal.icon} {goal.name}
              </span>
              <span className="shrink-0 text-muted">
                {remaining > 0 ? `${remaining.toLocaleString()} to go${weeks ? ` · ~${weeks} wk${weeks === 1 ? "" : "s"}` : ""}` : "Ready"}
              </span>
            </div>
            <ProgressBar value={goal.progress} color="var(--coin)" className="mt-2 h-2" />
          </Card>
        </Link>
      )}

      {s.balance < 0 && (
        <p className="mt-3 rounded-2xl bg-violet-soft px-3 py-2 text-sm font-bold text-violet">
          You're at {s.balance}. {family.workItOffEnabled ? "Work-it-off jobs are listed first." : "Jobs you finish count toward getting back to zero."}
        </p>
      )}

      {due.length > 0 && (
        <>
          <SectionTitle>
            Responsibilities · {gate.done}/{gate.total}
          </SectionTitle>
          {left.length > 0 && <p className="-mt-1 mb-2 px-1 text-sm text-muted">Tap one when you've done it.</p>}
          <div className="space-y-2">
            {/* Done ones collapse into the count, so this list only shows what's left (or needs a redo). */}
            {left.map((i) => (
              <ResponsibilityRow key={i.responsibility.id} item={i} />
            ))}
            {left.length === 0 && <p className="px-1 text-sm font-semibold text-muted">All done.</p>}
          </div>
        </>
      )}

      {active.length > 0 && (
        <>
          <SectionTitle>In progress</SectionTitle>
          <div className="space-y-2">
            {active.map((j) => (
              <JobCard key={j.id} job={j} compact />
            ))}
          </div>
        </>
      )}

      <SectionTitle
        action={
          <Link to="/kid/jobs" className="flex items-center text-sm font-bold text-brand">
            All jobs <ChevronRight className="size-4" />
          </Link>
        }
      >
        Best pay right now
      </SectionTitle>
      {open.length === 0 ? (
        <p className="px-1 text-sm text-muted">
          Nothing open.{" "}
          <Link to="/kid/jobs" className="font-bold text-brand">
            Pitch a job
          </Link>
        </p>
      ) : (
        <div className="space-y-2">
          {open.slice(0, 3).map((j) => (
            <JobCard key={j.id} job={j} compact />
          ))}
        </div>
      )}
    </div>
  );
}
